import { useState } from "react";
import { Image } from "antd";
import { toast } from "react-toastify";
import { DeleteIcon } from "themes/Icon";
import ModalComp from "themes/elements/Modal";
import TagComp from "themes/elements/Tag";
import { isPermit } from "utils/permissionCheck";
import ButtonComp from "themes/elements/Button";
import { deleteNotification } from "../../../api/notification";
import DeleteNotification from "../DeleteNotification";
import style from "./notification.module.scss";

function NotificationDetails({
  open,
  setOpen,
  data,
  handleCancel,
  loadNotifications,
  tabState,
  onEdit,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [isDelete, setDelete] = useState(false);

  const statusFlg = data?.status_flg;

  const handleDeleteNotification = async (id) => {
    setIsLoading(true);
    try {
      const response = await deleteNotification({
        notification_content_id: [id],
      });
      if (response.statusCode === 200) {
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
      loadNotifications();
      setOpen(false);
      setIsLoading(false);
      setDelete(false);
    } catch (error) {
      setIsLoading(false);
      toast.error("Error deleting notification");
    }
  };

  const getRejectionReason = () => {
    if (statusFlg !== 3) return null;
    const activities = data?.activity || data?.activities;
    if (Array.isArray(activities)) {
      const rejectEntry = activities.find(
        (a) => a?.news_status_remarks && a?.news_status_remarks !== ""
      );
      if (rejectEntry) return rejectEntry.news_status_remarks;
    }
    if (data?.remarks) return data.remarks;
    if (data?.rejection_reason) return data.rejection_reason;
    return null;
  };

  const renderFooter = () => {
    // Submitted (status 1): close only
    if (statusFlg === 1) {
      return (
        <div className="flex align-center gap-2 justify-end">
          <ButtonComp variant="secondary" onClick={() => setOpen(false)}>
            Close
          </ButtonComp>
        </div>
      );
    }

    // Rejected without resubmission: delete only
    if (statusFlg === 3 && !data?.allow_resubmission_flg) {
      return (
        <div className="flex align-center gap-2 justify-end">
          {isPermit(4, "delete") && (
            <ButtonComp
              variant="secondary"
              onClick={() => setDelete(true)}
              prefix={<DeleteIcon />}
            >
              Delete
            </ButtonComp>
          )}
        </div>
      );
    }

    // Rejected with resubmission: edit + delete
    if (statusFlg === 3 && data?.allow_resubmission_flg) {
      return (
        <div className="flex align-center gap-2 justify-end">
          {isPermit(4, "delete") && (
            <ButtonComp
              variant="secondary"
              onClick={() => setDelete(true)}
              prefix={<DeleteIcon />}
            >
              Delete
            </ButtonComp>
          )}
          <ButtonComp variant="secondary" onClick={() => onEdit?.(data)}>
            Edit
          </ButtonComp>
        </div>
      );
    }

    // Published (4) / Expired (6): delete only
    if (statusFlg === 4 || statusFlg === 6) {
      return (
        <div className="flex align-center gap-2 justify-end">
          {isPermit(4, "delete") && (
            <ButtonComp
              variant="secondary"
              onClick={() => setDelete(true)}
              prefix={<DeleteIcon />}
            >
              Delete
            </ButtonComp>
          )}
        </div>
      );
    }

    // Scheduled (5) / Drafts (0): edit + delete
    return (
      <div className="flex align-center gap-2 justify-end">
        {isPermit(4, "delete") && (
          <ButtonComp
            variant="secondary"
            onClick={() => setDelete(true)}
            prefix={<DeleteIcon />}
          >
            Delete
          </ButtonComp>
        )}
        <ButtonComp variant="secondary" onClick={() => onEdit?.(data)}>
          Edit
        </ButtonComp>
      </div>
    );
  };

  const allFiles = data?.files || [];
  const imageFiles = allFiles.filter((f) =>
    f?.notification_file_mimetype?.startsWith("image/")
  );
  const otherFiles = allFiles.filter(
    (f) => !f?.notification_file_mimetype?.startsWith("image/")
  );
  const hasAttachments = imageFiles.length > 0 || otherFiles.length > 0;
  const wardsList = Array.isArray(data?.wards) ? data.wards : [];

  const getFileIcon = (mimeType) => {
    if (!mimeType) return null;
    if (mimeType === "application/pdf")
      return (
        <img
          src="https://cdn-icons-png.flaticon.com/512/337/337946.png"
          alt="pdf"
          width={32}
          height={32}
        />
      );
    if (mimeType.startsWith("video/"))
      return (
        <img
          src="https://cdn-icons-png.flaticon.com/512/1161/1161738.png"
          alt="video"
          width={32}
          height={32}
        />
      );
    return null;
  };

  const handleDownload = (file) => {
    const link = document.createElement("a");
    link.href = file?.notification_s3_path;
    link.download = file?.notification_file_name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const rejectionReason = getRejectionReason();

  const detailsInner = (
    <div
      className={`flex flex-column gap-2 ${hasAttachments ? "" : style["modal-balanced"]}`}
    >
      <h5 className={style["content-title"]}>{data?.title}</h5>
      <div className="flex flex-column gap-4">
        <div className={style["content-desc"]}>
          <h6 className={style["data-value"]}>{data?.content}</h6>
        </div>

        <div className="flex align-center gap-2">
          <p className={`${style["content-desc"]} ${style["key-value"]}`}>
            Type:
          </p>
          <h6 className={style["data-value"]}>{data?.category}</h6>
        </div>

        <div className="flex align-start gap-2">
          <p className={`${style["content-desc"]} ${style["key-value"]}`}>
            Recipients:
          </p>
          <div className={style["recipients-value"]}>
            {wardsList.length > 0 ? (
              <div className="flex align-center wrap gap-2">
                {wardsList.map((ward, index) => (
                  <TagComp key={index} height={22}>
                    {`${ward.ward_code} - ${ward.region_name}`}
                  </TagComp>
                ))}
              </div>
            ) : Number(data?.recipients_flg) === 1 ||
              (typeof data?.recipients === "string" &&
                /all citizens/i.test(data.recipients.trim())) ? (
              <TagComp height={22}>All citizens</TagComp>
            ) : typeof data?.recipients === "string" &&
              data.recipients.trim() !== "" ? (
              <TagComp height={22}>{data.recipients.trim()}</TagComp>
            ) : (
              <TagComp height={22}>All citizens</TagComp>
            )}
          </div>
        </div>

        {data?.start_date_time && (
          <div className="flex align-center gap-2">
            <p className={`${style["content-desc"]} ${style["key-value"]}`}>
              Start date & time:
            </p>
            <h6 className={style["data-value"]}>{data.start_date_time}</h6>
          </div>
        )}

        {data?.end_date_time && (
          <div className="flex align-center gap-2">
            <p className={`${style["content-desc"]} ${style["key-value"]}`}>
              End date & time:
            </p>
            <h6 className={style["data-value"]}>{data.end_date_time}</h6>
          </div>
        )}

        {statusFlg === 4 && data?.published_date_time && (
          <div className="flex align-center gap-2">
            <p className={`${style["content-desc"]} ${style["key-value"]}`}>
              Published date & time:
            </p>
            <h6 className={style["data-value"]}>{data.published_date_time}</h6>
          </div>
        )}

        {statusFlg === 5 && data?.alert_date_time && (
          <div className="flex align-center gap-2">
            <p className={`${style["content-desc"]} ${style["key-value"]}`}>
              Scheduled on:
            </p>
            <h6 className={style["data-value"]}>{data.alert_date_time}</h6>
          </div>
        )}

        {statusFlg !== 4 && statusFlg !== 5 && data?.alert_date_time && (
          <div className="flex align-center gap-2">
            <p className={`${style["content-desc"]} ${style["key-value"]}`}>
              Alert date:
            </p>
            <h6 className={style["data-value"]}>{data.alert_date_time}</h6>
          </div>
        )}

        {data?.expiry_date_time && (
          <div className="flex align-center gap-2">
            <p className={`${style["content-desc"]} ${style["key-value"]}`}>
              Expiry date & time:
            </p>
            <h6 className={style["data-value"]}>{data.expiry_date_time}</h6>
          </div>
        )}

        {data?.destination && data.destination !== "None" && (
          <div className={`flex flex-column gap-4 ${style["destination-container"]}`}>
            <div className="flex align-center gap-2 mt-4">
              <p className={`${style["content-desc"]} ${style["key-value"]}`}>
                Destination:
              </p>
              <h6 className={style["data-value"]}>{data.destination}</h6>
            </div>
            {data?.redirection_type && (
              <div className="flex align-center gap-2">
                <p className={`${style["content-desc"]} ${style["key-value"]}`}>
                  {data.destination_type == 1 ? "In-app screen:" : "External URL:"}
                </p>
                <h6 className={style["data-value"]}>{data.external_link}</h6>
              </div>
            )}
            {data?.display_us && (
              <div className="flex align-center gap-2">
                <p className={`${style["content-desc"]} ${style["key-value"]}`}>
                  Display as:
                </p>
                <h6 className={style["data-value"]}>{data.display_us}</h6>
              </div>
            )}
          </div>
        )}

        {statusFlg === 3 && rejectionReason && (
          <div
            className="flex flex-column gap-1"
            style={{
              background: "#FFF1F0",
              border: "1px solid #FFA39E",
              borderRadius: 8,
              padding: "12px 16px",
              marginTop: 8,
            }}
          >
            <p style={{ fontWeight: 600, color: "#CF1322", fontSize: 14 }}>
              Reason for rejection
            </p>
            <p style={{ color: "#434343", fontSize: 14 }}>
              {rejectionReason}
            </p>
          </div>
        )}

        {imageFiles.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <p className={`${style["content-desc"]} ${style["key-value"]}`} style={{ marginBottom: 8 }}>
              Attachments
            </p>
            <Image.PreviewGroup>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {imageFiles.map((file, index) => (
                  <Image
                    key={file?.notification_media_id || index}
                    src={file?.notification_s3_path}
                    alt={file?.notification_file_name}
                    width={imageFiles.length === 1 ? "100%" : 120}
                    height={imageFiles.length === 1 ? "auto" : 120}
                    style={{
                      objectFit: "cover",
                      borderRadius: 8,
                      maxHeight: imageFiles.length === 1 ? 300 : 120,
                    }}
                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88P/BfwAJhAPk2tG8fAAAAABJRU5ErkJggg=="
                  />
                ))}
              </div>
            </Image.PreviewGroup>
          </div>
        )}

        {otherFiles.length > 0 && (
          <div style={{ marginTop: imageFiles.length > 0 ? 12 : 16 }}>
            {imageFiles.length === 0 && (
              <p className={`${style["content-desc"]} ${style["key-value"]}`} style={{ marginBottom: 8 }}>
                Attachments
              </p>
            )}
            {otherFiles.map((file, index) => (
              <div
                key={file?.notification_media_id || index}
                style={{
                  background: "#f5f5f5",
                  padding: "12px 16px",
                  borderRadius: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {getFileIcon(file?.notification_file_mimetype)}
                  <div>
                    <div style={{ fontWeight: 500 }}>{file?.notification_file_name}</div>
                    {file?.notification_file_size > 0 && (
                      <div style={{ fontSize: 12, color: "#555" }}>
                        {file.notification_file_size_formatted || `${(file.notification_file_size / 1024).toFixed(1)} KB`}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  style={{
                    border: "none",
                    background: "none",
                    textDecoration: "underline",
                    cursor: "pointer",
                    color: "var(--Dark, #121212)",
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "130%",
                  }}
                  onClick={() => handleDownload(file)}
                >
                  Download
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <ModalComp
        handleCancel={handleCancel}
        title={data?.notification_type === 2 || data?.category === "Alert" ? "Alert details" : "Notification details"}
        footer={renderFooter()}
        open={open}
        setOpen={setOpen}
        width={hasAttachments ? 720 : 520}
      >
        {detailsInner}
      </ModalComp>
      <DeleteNotification
        data={data}
        isLoading={isLoading}
        open={isDelete}
        setOpen={setDelete}
        handleDeleteNotification={() =>
          handleDeleteNotification(Number(data.id))
        }
      />
    </>
  );
}

export default NotificationDetails;
